'use client';

import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from '../core/Loading';
import { 
  Box, 
  Typography, 
  Button, 
  FormControlLabel, 
  Switch, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import { formatAddress, removeExtension } from '@/stack-shared/utils/gen-utils';
import { StandardUploadInput } from '../form/StandardUploadInput';
import { getFileExtension } from '@/utils/general';
import { FILE_CHUNK_SIZE, RESOURCE_TYPE, UPLOAD_COLLECTION_METHOD } from '@/stack-shared/constants/resource-constants';
import { useUploadResourceFile } from '@/hooks/api/useResources';
import { useUpdateGoaAsset } from '@/hooks/api/useGoas';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPickerSkinny';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import GenericFormModal from '../form/GenericFormModal';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthData } from '@/hooks/getAuthData';
import { useNotification } from '@/contexts/NotificationContext';

import LocationVisitsTable from '../location-visit/LocationVisitsTable';
import LocationContactTable from './LocationContactTable';
import LocationRejectsTable from '../reject/LocationRejectsTable';
import TitleBar from '../layout/TitleBar';
import { useLocationDetails, useReorderGoas, useUpdateLocation } from '@/hooks/useLocation';
import { US_STATES } from '@/constants/form.constants';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import MapIcon from '@mui/icons-material/Map';
import BuildIcon from '@mui/icons-material/Build';
import { IconButton } from '@mui/material';

const LocationPage: React.FC = () => {

  const { locationId } = useParams();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const queryClient = useQueryClient();
  const { roles } = useAuthData();
  const hasAdminRole = roles.some((x) => x.name === 'Admin');
  const uploadResourceFileMutation = useUploadResourceFile();
  const updateGoaAssetMutation = useUpdateGoaAsset();
  const { showSuccess, showError } = useNotification();

  const navigate = useNavigate();
  const [reorderGoasVisible, setReorderGoasVisible] = useState(false);
  const [addressEditModalVisible, setAddressEditModalVisible] = useState(false);
  const updateLocationMutation = useUpdateLocation();

  // Use React Query hooks
  const { data: locationData, isLoading, error } = useLocationDetails(locationId!);
  const reorderGoasMutation = useReorderGoas();

  // Console log the data to see what we get
  console.log('Location details data:', locationData);
  console.log('Location details loading:', isLoading);
  console.log('Location details error:', error);

  // Get data from React Query instead of Redux
  const location = locationData?.location;
  const address = locationData?.address;
  const atms = locationData?.atms || [];
  const allGoas = locationData?.goas || [];
  const trks = locationData?.trks || [];
  const ctms = locationData?.ctms || [];
  const mainPhoto = locationData?.resource;
  const contacts = locationData?.contacts || [];
  const locationContacts = locationData?.locationContacts || [];

  // Filter GOAs based on showInactive state
  const goas = useMemo(() => {
    if (showInactive) {
      return allGoas;
    }
    return allGoas.filter((goa: any) => goa.active !== false);
  }, [allGoas, showInactive]);

  // Add this debug log
  console.log('Contacts with isPrimary:', contacts);
  console.log('Location Contacts raw:', locationContacts);


  if (isLoading) {
    return <Loading loading={true} noBackdrop />;
  }

  if (error) {
    return <div>Error loading location details</div>;
  }

  if (!location) {
    return <Loading loading={true} noBackdrop />;
  }

  const onFileSelected = (files: FileList) => {
    setSelectedFile(files[0]);
    handleSubmit(files[0]);
  };

  const handleSubmit = (selectedFile: File) => {
    const parts = Math.ceil(selectedFile!.size / FILE_CHUNK_SIZE);
    try {
      getFileExtension(selectedFile!.name);
    } catch (e) {
      showError('Invalid File Extension.');
      return;
    }

    const uploadData = {
      name: removeExtension(selectedFile.name),
      model: 'location-main-photo',
      modelId: locationId,
      extension: getFileExtension(selectedFile!.name),
      fileName: locationId + "-photo." + getFileExtension(selectedFile!.name),
      originalFileName: selectedFile!.name,
      uploadCollectionMethod: UPLOAD_COLLECTION_METHOD.REPLACE,
      type: RESOURCE_TYPE.IMAGE,
      parts: 1,
      file: selectedFile,
      rootPath: 'location',
      primary: true,
      conversions: [],
      fileSize: selectedFile!.size
    };
    uploadResourceFileMutation.mutate(uploadData, {
      onSuccess: () => {
        showSuccess('File uploaded.');
        queryClient.invalidateQueries({ queryKey: ['location-details', locationId] });
      },
      onError: (error: any) => {
        showError(error.message || 'File upload failed.');
      },
    });
  };

  const reorderGoaFields = [
    {
      groupName: "Reorder Assets",
      fields: goas.flatMap((x) => [
        {
          label: "",
          name: `${x.id}_display`,
          component: <div>{x.assetId}</div>,
          type: GENERIC_FORM_FIELD_TYPE.REACT_ELEMENT,
          required: false,
          columns: 6,
        },
        {
          label: "",
          name: `${x.id}`,
          type: GENERIC_FORM_FIELD_TYPE.NUMBER,
          required: true,
          columns: 6,
        },
      ]),
    },
  ];


  const handleActivateGoa = (goa: any) => {
    updateGoaAssetMutation.mutate({ id: goa.id, active: true }, {
      onSuccess: () => {
        showSuccess('GOA activated successfully');
        queryClient.invalidateQueries({ queryKey: ['location-details', locationId] });
      },
      onError: (error: any) => {
        showError(error.message || 'Failed to activate GOA');
      },
    });
  };

  const handleDeactivateGoa = (goa: any) => {
    updateGoaAssetMutation.mutate({ id: goa.id, active: false }, {
      onSuccess: () => {
        showSuccess('GOA deactivated successfully');
        queryClient.invalidateQueries({ queryKey: ['location-details', locationId] });
      },
      onError: (error: any) => {
        showError(error.message || 'Failed to deactivate GOA');
      },
    });
  };


  // Compact List Table Components
  const CompactGoaList = ({ items }: { items: any[] }) => {
    if (items.length === 0) return null;
    
    const sortedItems = [...items].sort((a, b) => {
      // Sort by position if available, otherwise by assetId
      if (a.position !== null && b.position !== null) {
        return a.position - b.position;
      }
      if (a.position !== null) return -1;
      if (b.position !== null) return 1;
      return (a.assetId || '').localeCompare(b.assetId || '');
    });
    
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>GOAs ({items.length})</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label="Show Inactive"
          />
        </Box>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>GOA ID</strong></TableCell>
                <TableCell><strong>Position</strong></TableCell>
                <TableCell><strong>Active</strong></TableCell>
                {hasAdminRole && <TableCell><strong>Actions</strong></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedItems.map((goa) => (
                <TableRow key={goa.id} hover>
                  <TableCell>
                    <Typography
                      component="span"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 }
                      }}
                      onClick={() => navigate(`/app/goas/${goa.id}`)}
                    >
                      {goa.assetId}
                    </Typography>
                  </TableCell>
                  <TableCell>{goa.position !== null ? goa.position : '-'}</TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        color: goa.active === false ? '#f44336' : '#4caf50',
                        fontWeight: 'bold'
                      }}
                    >
                      {goa.active === false ? 'Inactive' : 'Active'}
                    </Typography>
                  </TableCell>
                  {hasAdminRole && (
                    <TableCell>
                      <GenericActionPicker
                        row={goa}
                        actions={[
                          {
                            icon: GENERIC_ICON_NAME.REORDER,
                            label: "Reorder",
                            onClick: () => setReorderGoasVisible(true),
                          },
                          ...(goa.active === false
                            ? [{
                                icon: GENERIC_ICON_NAME.CHECK_CIRCLE,
                                label: "Activate",
                                onClick: () => handleActivateGoa(goa),
                              }]
                            : [{
                                icon: GENERIC_ICON_NAME.BLOCK,
                                label: "Deactivate",
                                onClick: () => handleDeactivateGoa(goa),
                              }]
                          ),
                        ]}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const CompactTrkList = ({ items }: { items: any[] }) => {
    if (items.length === 0) return null;
    
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>TRKs ({items.length})</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>TRK ID</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((trk) => (
                <TableRow key={trk.id} hover>
                  <TableCell>{trk.assetId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const CompactAtmList = ({ items }: { items: any[] }) => {
    if (items.length === 0) return null;
    
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>ATMs ({items.length})</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>ATM ID</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((atm) => (
                <TableRow key={atm.id} hover>
                  <TableCell>{atm.assetId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const CompactCtmList = ({ items }: { items: any[] }) => {
    if (items.length === 0) return null;
    
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>CTMs ({items.length})</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>CTM ID</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((ctm) => (
                <TableRow key={ctm.id} hover>
                  <TableCell>{ctm.assetId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };


  const initialGoaPositionValues = goas.reduce((acc: any, x) => {
    acc[`${x.id}`] = x.position || 0; // Initial value for position input (empty string or default number)
    return acc;
  }, {});

  // Location edit fields - shared with LocationsPage
  const locationFields = [
    {
      groupName: 'Location',
      fields: [
        { label: 'Location Name', name: 'name', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 },
        { label: 'Active', name: 'active', type: GENERIC_FORM_FIELD_TYPE.SWITCH, required: false, columns: 12 },
        { label: 'Address Line 1', name: 'addressLine1', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        { label: 'Address Line 2', name: 'addressLine2', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
        { label: 'City', name: 'city', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
        { label: 'State', name: 'subdivision', type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT, required: false, columns: 6, options: US_STATES },
        { label: 'Zip Code', name: 'postalCode', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
        { label: 'Country', name: 'country', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
        { label: 'Commission Percentage', name: 'commissionPercentage', type: GENERIC_FORM_FIELD_TYPE.FLOAT, required: false, columns: 6, validation: Yup.number().min(0).max(100).transform((value) => (isNaN(value) ? undefined : value)) },
      ],
    },
  ];

  // Generate Google Maps URL
  const getGoogleMapsUrl = () => {
    if (!address) return null;
    const addressParts = [
      address.addressLine1,
      address.addressLine2,
      address.city,
      address.subdivision,
      address.postalCode,
      address.country,
    ].filter(Boolean);
    
    if (addressParts.length === 0) return null;
    
    const query = encodeURIComponent(addressParts.join(', '));
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  // Use the same update logic as LocationsPage
  const handleLocationUpdate = async (values: any) => {
    if (!location || !location.id) {
      console.error('Cannot update: location or location.id is missing', { location });
      showError('Error: Location ID is missing');
      return;
    }
    
    // Ensure id is always present and not overwritten by form values
    // Remove any id from values to prevent conflicts, then explicitly set it
    const { id: _, ...valuesWithoutId } = values;
    const updateData = {
      id: location.id, // Always use the location.id from the current location
      ...valuesWithoutId,
    };
    
    console.log('Updating location with data:', updateData);
    console.log('Location ID being used:', location.id);
    
    updateLocationMutation.mutate(updateData, {
      onSuccess: () => {
        // Close modal immediately - don't wait for anything
        setAddressEditModalVisible(false);
        showSuccess('Location updated successfully');
        
        // Cache invalidation and refetch is handled by useUpdateLocation hook
        // No need to do it here - let it happen in the background
      },
      onError: (error) => {
        showError('Failed to update location');
        console.error('Failed to update location:', error);
      },
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Locations', href: '/app/locations' },
    { label: location.name },
  ];

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title={location.name} />
      <GenericFormModal
        visible={reorderGoasVisible}
        onDismiss={() => setReorderGoasVisible(false)}
        onSubmit={async (values: any) => {
          console.log("values?", values);

          const goaOrderValues = [] as any[]
          Object.keys(values).forEach((key) => {
            goaOrderValues.push({ id: key, position: values[key] });
          })

          try {
            await reorderGoasMutation.mutateAsync({ goas: goaOrderValues });
            setReorderGoasVisible(false);
            // No need for tableRefreshKey anymore - React Query will handle the refresh
          } catch (error) {
            console.error('Failed to reorder GOAs:', error);
          }
        }}
        initialValues={initialGoaPositionValues}
        fieldGroups={reorderGoaFields}
        title={'Reorder GOAs'}
      />

      <GenericFormModal
        visible={addressEditModalVisible}
        onDismiss={() => setAddressEditModalVisible(false)}
        onSubmit={handleLocationUpdate}
        initialValues={{ ...location, ...(address || {}) }}
        fieldGroups={locationFields}
        title={'Edit Location'}
      />

      <Box>
        {/* Location Overview Card */}
        <Box
          sx={{
            width: {
              xs: '100%',
              lg: '50%'
            },
            mb: 2
          }}
        >
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Address Section */}
                {address && formatAddress(address) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                      {formatAddress(address)}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      No address set
                    </Typography>
                  </Box>
                )}

                {/* Image */}
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <StandardUploadInput
                    showPreview={true}
                    onFileSelected={onFileSelected}
                    handleSubmit={handleSubmit}
                    initialImage={mainPhoto?.signedUrl}
                    accept={'*'}
                    uploadType={RESOURCE_TYPE.IMAGE}
                    isThumbnail={true}
                  />
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setAddressEditModalVisible(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Edit Location
                  </Button>
                  {getGoogleMapsUrl() && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MapIcon />}
                      component="a"
                      href={getGoogleMapsUrl()!}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: 'none' }}
                    >
                      Maps
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<BuildIcon />}
                    onClick={() => navigate(`/app/location-service/${locationId}`)}
                    sx={{ textTransform: 'none' }}
                  >
                    Service
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Location Visits Table */}
        <Box sx={{ mb: 2 }}>
          <LocationVisitsTable locationId={locationId!} isAdmin={hasAdminRole} />
        </Box>

        {/* Compact Asset Lists */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: '1fr',
              xl: '1fr 1fr'
            },
            gap: 2,
            mt: 2
          }}
        >
          <CompactGoaList items={goas} />
          <CompactAtmList items={atms} />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              lg: '1fr',
              xl: '1fr 1fr'
            },
            gap: 2,
            mt: 2
          }}
        >
          <CompactTrkList items={trks} />
          <CompactCtmList items={ctms} />
        </Box>

        {/* Contacts and Rejects Tables */}
        <LocationContactTable locationId={locationId!} />
        <LocationRejectsTable locationId={locationId!} />

      </Box>
    </>

  );
};



export default LocationPage;
